import { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Store, MapPin, Plus, Star } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import { optimizeCloudinaryUrl } from '../lib/cloudinary';
import { RatingBadge, useBulkRatings } from './RatingBadge';

const formatPrice = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const ProductMiniCard = ({ product, rating }) => {
  const { addItem, items, storeId } = useCart();
  const hasPromo = product.promo_price && product.promo_price < product.price;
  const discount = hasPromo
    ? Math.round((1 - product.promo_price / product.price) * 100)
    : 0;
  const hasVariations = (product.variations || []).length > 0;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (storeId && storeId !== product.store_id && items.length > 0) {
      toast.error('Você já tem itens de outra loja no carrinho. Finalize ou limpe antes.');
      return;
    }
    if (hasVariations) {
      // Force user to pick variations on the product page
      window.location.href = `/produto/${product.id}`;
      return;
    }
    addItem(product);
    toast.success(`${product.name} adicionado ao carrinho`);
  };

  return (
    <Link
      to={`/produto/${product.id}`}
      className="group flex-shrink-0 w-40 sm:w-44 snap-start bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      data-testid={`carousel-product-${product.id}`}
    >
      <div className="relative aspect-square bg-slate-50">
        <img
          src={optimizeCloudinaryUrl(product.images?.[0], { width: 400 }) || 'https://via.placeholder.com/200x200?text=Produto'}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {hasPromo && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow" data-testid={`carousel-promo-${product.id}`}>
            -{discount}%
          </span>
        )}
        {product.featured && (
          <span className="absolute top-2 right-2 bg-amber-400 text-blue-900 text-[10px] font-bold px-2 py-0.5 rounded-full shadow inline-flex items-center gap-1">
            <Star className="w-3 h-3 fill-blue-900" /> Oferta
          </span>
        )}
        <button
          onClick={handleAdd}
          className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shadow-lg transition-transform active:scale-95"
          data-testid={`carousel-add-${product.id}`}
          aria-label="Adicionar ao carrinho"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 min-h-[2.5rem] leading-tight">
          {product.name}
        </h3>
        <div className="mt-1 min-h-[1.25rem]">
          {rating && rating.count > 0 && (
            <RatingBadge value={rating.avg} count={rating.count} size="xs" />
          )}
        </div>
        <div className="mt-1.5">
          {hasPromo ? (
            <div className="flex flex-col">
              <span className="text-[11px] text-slate-400 line-through leading-none">
                {formatPrice(product.price)}
              </span>
              <span className="text-base font-bold text-green-600 leading-tight">
                {formatPrice(product.promo_price)}
              </span>
            </div>
          ) : (
            <span className="text-base font-bold text-slate-900">
              {formatPrice(product.price)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export const StoreProductCarousel = ({ store, products }) => {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const [overflows, setOverflows] = useState(false);
  const ratingMap = useBulkRatings(
    [{ target_type: 'store', target_id: store.id }].concat(
      products.map((p) => ({ target_type: 'product', target_id: p.id }))
    )
  );
  const storeRating = ratingMap[`store:${store.id}`];

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measurePitch = () => {
      const firstCard = el.querySelector('[data-testid^="carousel-product-"]');
      if (!firstCard) return el.clientWidth || 1;
      const style = window.getComputedStyle(el);
      const gap = parseFloat(style.columnGap || style.gap || '12') || 12;
      return firstCard.offsetWidth + gap;
    };
    const onScroll = () => {
      const { scrollLeft, clientWidth, scrollWidth } = el;
      setOverflows(scrollWidth - clientWidth > 4);
      setShowLeft(scrollLeft > 8);
      setShowRight(scrollLeft + clientWidth < scrollWidth - 8);
      const pitch = measurePitch();
      const idx = Math.min(products.length - 1, Math.max(0, Math.round(scrollLeft / pitch)));
      setActiveIndex(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    const ro = new ResizeObserver(onScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [products.length]);

  const scrollByDir = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const delta = Math.round(el.clientWidth * 0.75) * (dir === 'next' ? 1 : -1);
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`store-carousel-${store.id}`}>
      {/* Store header */}
      <Link
        to={`/loja/${store.id}`}
        className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
        data-testid={`store-carousel-header-${store.id}`}
      >
        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {store.logo_url ? (
            <img
              src={optimizeCloudinaryUrl(store.logo_url, { width: 200 })}
              alt={store.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <Store className="w-6 h-6 text-blue-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-slate-900 truncate">{store.name}</h3>
            {storeRating && storeRating.count > 0 && (
              <RatingBadge value={storeRating.avg} count={storeRating.count} size="xs" />
            )}
          </div>
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {store.city}/{store.state} · {products.length} {products.length === 1 ? 'produto' : 'produtos'}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </Link>

      {/* Carousel */}
      <div className="relative">
        <div
          ref={scrollerRef}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar px-4 py-3"
          data-testid={`store-carousel-scroller-${store.id}`}
        >
          {products.map((p) => (
            <ProductMiniCard
              key={p.id}
              product={{ ...p, store_name: store.name }}
              rating={ratingMap[`product:${p.id}`]}
            />
          ))}
        </div>

        {/* Arrows (only visible on wider screens or when scrollable) */}
        {showLeft && (
          <button
            onClick={() => scrollByDir('prev')}
            className="hidden sm:flex absolute left-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-lg border border-slate-200 items-center justify-center text-slate-700 hover:bg-slate-50 transition-colors"
            data-testid={`store-carousel-prev-${store.id}`}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {showRight && products.length > 2 && (
          <button
            onClick={() => scrollByDir('next')}
            className="hidden sm:flex absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-lg border border-slate-200 items-center justify-center text-slate-700 hover:bg-slate-50 transition-colors"
            data-testid={`store-carousel-next-${store.id}`}
            aria-label="Próximo"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Dots indicators — only when there's actual overflow */}
      {overflows && products.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 pb-3" data-testid={`store-carousel-dots-${store.id}`}>
          {products.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === activeIndex ? 'bg-blue-600 w-4' : 'bg-slate-300 w-1.5'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StoreProductCarousel;
